import { SeoParameter } from '@atc/bonnet-paths';
import isObject from 'is-object';
async function getValue(key, seoParam, parsedQuery, originalQuery) {
  let value;
  switch (seoParam.param) {
    case 'trimCode':
      {
        // We require a make and model code to be present to decode trim codes
        // if one is not present then we want to bypass this
        if (parsedQuery.makeCode && parsedQuery.modelCode) {
          const trimCode = await seoParam.decodeValue(originalQuery[key], {
            makeCode: parsedQuery.makeCode,
            modelCode: parsedQuery.modelCode,
            seriesCode: parsedQuery.seriesCode
          });
          if (trimCode) {
            value = `${parsedQuery.modelCode}|${trimCode}`;
          }
        }
        break;
      }
    case 'modelCode':
      {
        // We require a make code to be present to decode model codes
        // if one is not present then we want to bypass this
        if (parsedQuery.makeCode && !parsedQuery.modelCode) {
          value = await seoParam.decodeValue(originalQuery[key], {
            makeCode: parsedQuery.makeCode
          });
        }
        break;
      }
    case 'seriesCode':
      {
        // We require a make code to be present to decode series codes
        // if one is not present then we want to bypass this
        if (parsedQuery.makeCode && !parsedQuery.seriesCode) {
          value = await seoParam.decodeValue(originalQuery[key], {
            makeCode: parsedQuery.makeCode
          });
        }
        break;
      }
    case 'seoHybridElectric':
      {
        value = ['HYB', 'ELE'];
        break;
      }
    case 'location':
      {
        if (originalQuery.zip) {
          value = await seoParam.decodeValue(originalQuery[key], {
            queryZip: originalQuery.zip
          });
        } else {
          value = await seoParam.decodeValue(originalQuery[key]);
        }
        break;
      }
    default:
      value = await seoParam.decodeValue(originalQuery[key]);
  }
  return value;
}
export default function parseSeo() {
  return async ctx => {
    const parsedQuery = await Object.keys(ctx.query).reduce(async (_acc, key) => {
      // resolve the async accumulator
      const acc = await _acc;

      // Create an seo parameter class
      const seoParam = new SeoParameter(key);

      // if the key IS an seo key then we need to extract the actual param
      if (seoParam.isSeoKey(key)) {
        // if the key is an seo key but the value is undefined then we delete it from the query.
        if (!ctx.query[key]) {
          delete ctx.query[key];
          return acc;
        }

        // if the param already exists in the query then we should ignore it
        // TODO: should this actually parse it an add it to the existing query?
        if (ctx.query[seoParam.param]) {
          return acc;
        }
        try {
          let value;
          let {
            param
          } = seoParam;
          if (seoParam.isMultiParameter) {
            const cachedValue = ctx.query[key];

            // delete the original key
            delete ctx.query[key];

            // iterate over each of the multiParameters
            // eslint-disable-next-line no-restricted-syntax
            for (const multiKey in seoParam.multiParameters) {
              if (Object.prototype.hasOwnProperty.call(seoParam.multiParameters, multiKey)) {
                // set the current key
                const currentKey = seoParam.multiParameters[multiKey];

                // create a new seo parameter based on the current key
                const currentSeoParam = new SeoParameter(currentKey);

                // set the cached seo value on the current key in the query
                ctx.query[currentKey] = cachedValue;

                // eslint-disable-next-line no-await-in-loop
                const currentValue = await getValue(currentKey, currentSeoParam, acc, ctx.query);

                // if we have a value for the current parameter
                // then set the key and value and param to current and break out
                if (currentValue) {
                  key = currentKey;
                  value = currentValue;
                  ({
                    param
                  } = currentSeoParam);
                  break;
                }

                // clean up the addition of the cached seo value
                // on the current key in the query
                // delete ctx.query[currentKey];
              }
            }
          } else {
            value = await getValue(key, seoParam, acc, ctx.query);
          }

          // If we have a decoded value then we want to add it to the updated ctx.query
          if (value) {
            // If seo decoding returns an object we should spread the result
            // on the updated query object
            // ex: location, price
            if (isObject(value)) {
              Object.assign(acc, {
                ...value
              });
            }

            // TODO: Should object values that were spread above be removed
            // We need to investigate if anyone is actually using those values today
            // FYD uses location today in its context middleware
            acc[param] = value;
          }
        } catch (err) {
          // TODO: Do we want to log out this error?
        }
        return acc;
      }

      // if the key IS NOT an seo key then replace it back in the accumulator untouched
      acc[key] = ctx.query[key];
      return acc;
    }, Promise.resolve({}));

    // replace the old query object witht the parsedVersion
    ctx.query = parsedQuery;

    // Register the middleware so that subsequent middleware can be aware
    ctx.registerMiddleware('parseSeo');
  };
}